import moment from 'moment';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublishFormMutation, useSaveAsDraftFormMutation } from '../../../../../../api/expenseAreas';
import {
    FormItem,
    OptionI,
    Section,
    SettingI,
    SupplierProfileQuestionaryComponentsI,
    SupplierProfileQuestionaryFormData
} from '../../../../../../api/types/expenseareas';
import type { BuilderPublishResult } from '../builderFormUtils';

function collectScoreCardValidationErrors(sections: Section[] | undefined) {
    const sectionErrors: string[] = [];
    const fieldsErrors: string[] = [];

    sections?.forEach((section) => {
        if (!section.title || !section.fields?.length) {
            sectionErrors.push(String(section?.localId));
        }

        section?.fields.forEach((field) => {
            const type = field.type;
            let validField = true;

            if (type === 'explanation-banner') {
                if (!field.label) {
                    fieldsErrors.push(String(field?.id));
                }
            }
            if (type === 'single-select' || type === 'slider') {
                if (!field.label) {
                    validField = false;
                } else if (!field?.options?.every((option) => option.value)) {
                    validField = false;
                }
            }
            if (!validField) {
                fieldsErrors.push(String(field?.id));
            }
        });
    });

    return { sectionErrors, fieldsErrors };
}

export default function useScoreCardController() {
    const location = useLocation();
    const ExpenseAreaId = useMemo(() => location.pathname.split('/')[2], [location.pathname]);

    const [isCantPublishModal, setIsCantPublishModal] = useState(false);
    const [sectionsError, setSectionsError] = useState<string[]>([]);
    const [fieldsError, setFieldsError] = useState<string[]>([]);
    const [selectedElement, setSelectedElement] = useState<null | string>(null);
    const [components, setComponents] = useState<SupplierProfileQuestionaryComponentsI | undefined>();

    const baseOptionData = components?.data?.field_option;

    const [data, setData] = useState<SupplierProfileQuestionaryFormData>({
        title: '',
        id: null,
        expense_area_id: null,
        type: '',
        status: '',
        description: '',
        user_id: null,
        sections: []
    });

    const [publishForm, { isSuccess: isSuccessPublishForm, isLoading: isLoadingPublished }] = usePublishFormMutation();

    const [saveAsDraft, { isSuccess: isSuccessSaveAsDraft, isLoading: isLoadingDraft }] = useSaveAsDraftFormMutation();

    const publish = (): BuilderPublishResult => {
        if (!data?.sections?.length) {
            setIsCantPublishModal(true);
            return { ok: false, reason: 'no-sections' };
        }
        const { sectionErrors, fieldsErrors: sectionFieldsErrors } = collectScoreCardValidationErrors(data?.sections);
        setSectionsError(sectionErrors);
        setFieldsError(sectionFieldsErrors);

        if (sectionErrors?.length || sectionFieldsErrors?.length) {
            return {
                ok: false,
                reason: 'invalid',
                sectionErrors,
                fieldsErrors: sectionFieldsErrors
            };
        }

        const baseData = {
                expense_area_id: ExpenseAreaId,
                type: 'score-card',
                data: {
                    ...data,
                    sections: data?.sections.map((section, index) => ({
                        ...section,
                        order: index,
                        id: typeof section?.id === 'string' ? null : section?.id,
                        fields: section.fields?.map((field, index) => {
                            const { linked_settings, ...restField } = field;

                            return {
                                ...restField,
                                order: index,
                                id: typeof field?.id === 'string' ? null : field?.id,

                                options: field?.options?.map((option) => {
                                    const { go_to_next_section_local, ...restOption } = option;
                                    return {
                                        ...restOption,
                                        label: option.value + '',
                                        value: option.value + '',
                                        go_to_next_section: field?.settings?.find(
                                            (setting) => setting?.type === 'go_to_section'
                                        )?.switchValue
                                            ? (() => {
                                                  const index = option?.go_to_next_section_local
                                                      ? data?.sections?.findIndex(
                                                            (section) =>
                                                                section?.localId + '' ===
                                                                option?.go_to_next_section_local + ''
                                                        )
                                                      : data?.sections?.findIndex(
                                                            (section) =>
                                                                section?.order + '' === option?.go_to_next_section + ''
                                                        );
                                                  return index !== -1 ? index : null;
                                              })()
                                            : null
                                    };
                                })
                            };
                        })
                    }))
                }
            };
            publishForm(baseData);
        return { ok: true };
    };

    const saveAsDraftFunc = () => {
        const baseData = {
            expense_area_id: ExpenseAreaId,
            type: 'score-card',
            data: {
                ...data,
                sections: data?.sections.map((section, index) => ({
                    ...section,
                    order: index,
                    id: typeof section?.id === 'string' ? null : section?.id,
                    fields: section.fields?.map((field, index) => {
                        const { linked_settings, ...restField } = field;

                        return {
                            ...restField,
                            order: index,
                            id: typeof field?.id === 'string' ? null : field?.id,
                            options: field?.options?.map((option) => {
                                const { go_to_next_section_local, ...restOption } = option;
                                return {
                                    ...restOption,
                                    label: option.value,
                                    go_to_next_section: field?.settings?.find(
                                        (setting) => setting?.type === 'go_to_section'
                                    )?.switchValue
                                        ? (() => {
                                              const index = option?.go_to_next_section_local
                                                  ? data?.sections?.findIndex(
                                                        (section) =>
                                                            section?.localId + '' ===
                                                            option?.go_to_next_section_local + ''
                                                    )
                                                  : data?.sections?.findIndex(
                                                        (section) =>
                                                            section?.order + '' === option?.go_to_next_section + ''
                                                    );
                                              return index !== -1 ? index : null;
                                          })()
                                        : null
                                };
                            })
                        };
                    })
                }))
            }
        };

        saveAsDraft(baseData);
    };

    const addNewSection = () => {
        const timestamp = moment().format('YYYYMMDDHHmmssSSS');

        const ID = `section-${timestamp}`;

        const newSection: Section = {
            id: ID,
            title: '',
            description: '',
            fields: [],
            localId: timestamp,
            order: data?.sections?.length
        };
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: [...prev.sections, newSection]
            };
        });
        setSelectedElement(ID);
    };

    function updateFieldOrSection(fieldName: keyof Section | keyof FormItem, newValue: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    updatedItems[itemIndex] = {
                        ...updatedItems[itemIndex],
                        [fieldName]: newValue
                    };
                    return { ...section, fields: updatedItems };
                })
            };
        });
    }

    function updateSettingField(fieldName: keyof SettingI, newValue: string | boolean | number, settingType: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) =>
                            s.type === settingType ? { ...s, [fieldName]: newValue } : s
                        )
                    };

                    return { ...section, fields: updatedItems };
                })
            };
        });
    }

    function updateOptionField(optionIndex: number, fieldName: keyof OptionI, newValue: string | boolean | number) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    if (!targetItem.options || optionIndex < 0 || optionIndex >= targetItem.options.length) {
                        return section;
                    }

                    const updatedOptions = [...targetItem.options];
                    updatedOptions[optionIndex] = {
                        ...updatedOptions[optionIndex],
                        [fieldName]: newValue
                    };

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function addOption() {
        setData((prev) => {
            if (!prev || !baseOptionData) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    const updatedOptions = targetItem.options ? [...targetItem.options] : [];

                    updatedOptions.push(baseOptionData);

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function updateOptionMultiSelectedInSetting(settingType: string, optionID: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            // если нет options — ничего не делаем
                            if (!s.options || s.options.length === 0) return s;

                            // находим индекс цели
                            const optionIndex = s.options.findIndex((opt) => opt.value === optionID);

                            // если не найден — возвращаем setting без изменений
                            if (optionIndex === -1) return s;

                            // иначе переключаем только у найденной опции поле selected
                            const updatedOptions = s.options.map((opt) =>
                                opt.value === optionID ? { ...opt, selected: !opt.selected } : opt
                            );

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function selectAllOptions(settingType: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            const updatedOptions = s.options
                                ? s.options.map((opt) => ({ ...opt, selected: true }))
                                : [];

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function deselectAllOptions(settingType: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            const updatedOptions = s.options
                                ? s.options.map((opt) => ({ ...opt, selected: false }))
                                : [];

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function removeOption(optionIndex: number) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    const updatedOptions = targetItem.options
                        ? targetItem.options.filter((_, idx) => idx !== optionIndex)
                        : [];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function removeItem() {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    return {
                        ...section,
                        fields: section.fields.filter((item) => item.id + '' !== selectedElement + '')
                    };
                })
            };
        });
    }

    function removeSection() {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.filter((section) => section.id !== selectedElement)
            };
        });
    }

    const findItem = (): FormItem | null => {
        for (const section of data?.sections) {
            const found = section?.fields?.find((item) => item.id + '' === selectedElement);
            if (found) return found;
        }

        return null;
    };

    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        if (
            target.classList.contains('main__form') ||
            target.classList.contains('builder__main') ||
            target.classList.contains('builder__header')
        ) {
            setSelectedElement(null);
        }
    });

    const maxSwitchValue = Math.max(0, ...(findItem()?.options?.map((elem) => Number(elem.rating) || 0) || []));

    const moveSectionToStart = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1) return prev;

            const [item] = arr.splice(index, 1);
            return {
                ...prev,
                sections: [item, ...arr]
            };
        });
    };

    const moveSectionToEnd = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1) return prev; // элемент не найден, ничего не делаем

            const [item] = arr.splice(index, 1); // удаляем элемент из массива
            return {
                ...prev,
                sections: [...arr, item] // добавляем в конец
            };
        });
    };

    const moveSectionDown = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1 || index === arr.length - 1) return prev; // элемент не найден или уже последний

            // меняем местами с следующим элементом
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];

            return {
                ...prev,
                sections: arr
            };
        });
    };

    const moveSectionUp = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index <= 0) return prev; // элемент не найден или уже первый

            // меняем местами с предыдущим элементом
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];

            return {
                ...prev,
                sections: arr
            };
        });
    };

    return {
        publish,
        saveAsDraftFunc,
        isSuccessSaveAsDraft,
        isLoadingDraft,
        data,
        setData,
        isCantPublishModal,
        setIsCantPublishModal,
        sectionsError,
        setSectionsError,
        fieldsError,
        setFieldsError,
        isSuccessPublishForm,
        ExpenseAreaId,
        isLoadingPublished,
        addNewSection,
        selectedElement,
        setSelectedElement,
        updateFieldOrSection,
        updateSettingField,
        updateOptionField,
        addOption,
        selectAllOptions,
        deselectAllOptions,
        removeOption,
        removeItem,
        removeSection,
        findItem,
        maxSwitchValue,
        moveSectionToStart,
        moveSectionToEnd,
        moveSectionDown,
        moveSectionUp,
        updateOptionMultiSelectedInSetting,
        setComponents
    };
}
